import React, { useEffect, useRef, useState, useContext } from "react";
import { useParams } from "react-router-dom";
import { useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import { 
  Grid, 
  LinearProgress, 
  Typography, 
  Button, 
  Divider, 
  Card, 
  CardHeader, 
  CardContent, 
  Box, 
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Avatar,
  Tooltip
} from "@material-ui/core";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import SendIcon from "@material-ui/icons/Send";
import PhoneIcon from "@material-ui/icons/Phone";
import MessageIcon from "@material-ui/icons/Message";
import ScheduleIcon from "@material-ui/icons/Schedule";
import EventAvailableIcon from "@material-ui/icons/EventAvailable";
import DoneAllIcon from "@material-ui/icons/DoneAll";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import ErrorIcon from "@material-ui/icons/Error";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import GetAppIcon from "@material-ui/icons/GetApp";
import ReplyIcon from "@material-ui/icons/Reply";
import { useDate } from "../../hooks/useDate";
import { AuthContext } from "../../context/Auth/AuthContext";
import api from "../../services/api";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title as ChartTitle,
  Tooltip as ChartTooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Doughnut, Bar } from 'react-chartjs-2';

// Registrar componentes do Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  ChartTitle,
  ChartTooltip,
  Legend,
  Filler
);

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
    marginBottom: theme.spacing(2),
  },
  summaryCards: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  summaryCard: {
    padding: theme.spacing(2),
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  cardIcon: {
    color: theme.palette.primary.main,
    fontSize: 40,
    marginBottom: theme.spacing(1),
  },
  progressContainer: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  tableContainer: {
    marginTop: theme.spacing(3),
  },
  tableTitle: {
    margin: theme.spacing(2, 0),
    fontWeight: 500,
  },
  chartContainer: {
    height: 300,
    marginBottom: theme.spacing(4),
  },
  pieChartContainer: {
    height: 250,
    position: 'relative',
    marginTop: theme.spacing(2),
  },
  sectionTitle: {
    fontWeight: 500,
    margin: theme.spacing(2, 0),
  },
  detailsCard: {
    marginBottom: theme.spacing(2),
  },
  avatar: {
    backgroundColor: theme.palette.primary.main,
    color: '#fff',
  },
}));

const MaturationReport = () => {
  const classes = useStyles();
  const history = useHistory();
  const { maturationId } = useParams();

  const [maturation, setMaturation] = useState({});
  const [loading, setLoading] = useState(false);
  const [messageRows, setMessageRows] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalSent, setTotalSent] = useState(0);
  const [totalDelivered, setTotalDelivered] = useState(0);
  const [totalError, setTotalError] = useState(0);
  const [totalPending, setTotalPending] = useState(0);
  const [uniqueNumbers, setUniqueNumbers] = useState(0);
  const [totalReceived, setTotalReceived] = useState(0);
  const mounted = useRef(true);
  const { user, socket } = useContext(AuthContext);

  const { datetimeToClient } = useDate();
  
  // Dados para o gráfico de pizza (status de envio)
  const [chartData, setChartData] = useState({
    labels: ['Entregues', 'Aguardando', 'Falhas'],
    datasets: [
      {
        data: [0, 0, 0],
        backgroundColor: [
          'rgba(75, 192, 192, 0.7)',
          'rgba(255, 206, 86, 0.7)',
          'rgba(255, 99, 132, 0.7)',
        ],
        borderColor: [
          'rgba(75, 192, 192, 1)',
          'rgba(255, 206, 86, 1)',
          'rgba(255, 99, 132, 1)',
        ],
        borderWidth: 1,
      },
    ],
  });

  // Dados para o gráfico de barras (envios vs recebimentos)
  const [barChartData, setBarChartData] = useState({
    labels: [],
    datasets: [
      {
        label: 'Enviadas',
        data: [],
        backgroundColor: 'rgba(54, 162, 235, 0.7)',
        borderColor: 'rgba(54, 162, 235, 1)',
        borderWidth: 1,
      },
      {
        label: 'Recebidas',
        data: [],
        backgroundColor: 'rgba(75, 192, 192, 0.7)',
        borderColor: 'rgba(75, 192, 192, 1)',
        borderWidth: 1,
      },
    ],
  });
  
  // Opções para os gráficos
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
      },
    },
  };

  const barChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
      },
    },
    scales: {
      y: {
        beginAtZero: true,
      },
    },
  };

  useEffect(() => {
    if (mounted.current) {
      findMaturation();
    }

    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (!user || !socket) return;

    const companyId = user.companyId;

    const onMaturationEvent = (data) => {
      if (data.record.id === +maturationId) {
        setMaturation(data.record);
        if (data.record.status === "FINALIZADA") {
          setTimeout(() => {
            findMaturation();
          }, 5000);
        }
      }
    };
    socket.on(`company-${companyId}-Maturation`, onMaturationEvent);

    return () => {
      socket.off(`company-${companyId}-Maturation`, onMaturationEvent);
    };
  }, [maturationId, user, socket]);

  const findMaturation = async () => {
    try {
      setLoading(true);
      
      // Buscar dados básicos da maturação
      const { data: maturationData } = await api.get(`/maturations/${maturationId}`);
      setMaturation(maturationData);
      
      // Buscar estatísticas
      try {
        const { data: stats } = await api.get(`/maturations/${maturationId}/stats`);
        setTotalSent(stats.totalSent || 0);
        setTotalDelivered(stats.totalDelivered || 0);
        setTotalError(stats.totalError || 0);
        setTotalPending(stats.totalPending || 0);
        setUniqueNumbers(stats.uniqueNumbers || 0);
        setTotalReceived(stats.totalReceived || 0);
        
        // Atualizar dados do gráfico de pizza
        setChartData({
          labels: ['Entregues', 'Aguardando', 'Falhas'],
          datasets: [
            {
              data: [stats.totalDelivered || 0, stats.totalPending || 0, stats.totalError || 0],
              backgroundColor: [
                'rgba(75, 192, 192, 0.7)',
                'rgba(255, 206, 86, 0.7)',
                'rgba(255, 99, 132, 0.7)',
              ],
              borderColor: [
                'rgba(75, 192, 192, 1)',
                'rgba(255, 206, 86, 1)',
                'rgba(255, 99, 132, 1)',
              ],
              borderWidth: 1,
            },
          ],
        });
      } catch (statsError) {
        console.error("Erro ao buscar estatísticas:", statsError);
        setTotalSent(0);
        setTotalDelivered(0);
        setTotalError(0);
        setTotalPending(0);
        setUniqueNumbers(0);
        setTotalReceived(0);
      }
      
      // Buscar dados de mensagens com paginação
      try {
        const { data: messagesResponse } = await api.get(`/maturations/${maturationId}/messages?page=1&pageSize=${rowsPerPage}`);
        
        if (messagesResponse && messagesResponse.messages) {
          setMessageRows(messagesResponse.messages);
          setTotalPages(messagesResponse.totalPages || 1);

          // Atualizar gráfico de barras (top 10 números)
          const topNumbers = messagesResponse.messages.slice(0, 10);
          setBarChartData({
            labels: topNumbers.map(m => m.number || m.name || 'Sem nome'),
            datasets: [
              {
                label: 'Enviadas',
                data: topNumbers.map(m => m.sentCount || 0),
                backgroundColor: 'rgba(54, 162, 235, 0.7)',
                borderColor: 'rgba(54, 162, 235, 1)',
                borderWidth: 1,
              },
              {
                label: 'Recebidas',
                data: topNumbers.map(m => m.receivedCount || 0),
                backgroundColor: 'rgba(75, 192, 192, 0.7)',
                borderColor: 'rgba(75, 192, 192, 1)',
                borderWidth: 1,
              },
            ],
          });
        }
      } catch (messagesError) {
        console.error("Erro ao buscar mensagens:", messagesError);
        setMessageRows([]);
      }
      
    } catch (error) {
      console.error("Erro ao buscar maturação:", error);
      toast.error("Erro ao carregar relatório da maturação");
    } finally {
      setLoading(false);
    }
  };

  const formatStatus = (val) => {
    switch (val) {
      case "INATIVA":
        return "Inativa";
      case "PROGRAMADA":
        return "Programada";
      case "EM_ANDAMENTO":
        return "Em Andamento";
      case "PAUSADA":
        return "Pausada";
      case "CANCELADA":
        return "Cancelada";
      case "FINALIZADA":
        return "Finalizada";
      default:
        return val;
    }
  };
  
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    loadMoreMessages(newPage + 1, rowsPerPage);
  };
  
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    loadMoreMessages(1, parseInt(event.target.value, 10));
  };
  
  const loadMoreMessages = async (newPage = 1, pageSize = 10) => {
    try {
      setLoading(true);
      
      const { data: messagesResponse } = await api.get(`/maturations/${maturationId}/messages?page=${newPage}&pageSize=${pageSize}`);
      
      if (messagesResponse && messagesResponse.messages) {
        setMessageRows(messagesResponse.messages);
        setTotalPages(messagesResponse.totalPages || 1);

        // Atualizar gráfico de barras
        const topNumbers = messagesResponse.messages.slice(0, 10);
        setBarChartData({
          labels: topNumbers.map(m => m.number || m.name || 'Sem nome'),
          datasets: [
            {
              label: 'Enviadas',
              data: topNumbers.map(m => m.sentCount || 0),
              backgroundColor: 'rgba(54, 162, 235, 0.7)',
              borderColor: 'rgba(54, 162, 235, 1)',
              borderWidth: 1,
            },
            {
              label: 'Recebidas',
              data: topNumbers.map(m => m.receivedCount || 0),
              backgroundColor: 'rgba(75, 192, 192, 0.7)',
              borderColor: 'rgba(75, 192, 192, 1)',
              borderWidth: 1,
            },
          ],
        });
      }
    } catch (error) {
      console.error("Erro ao carregar mensagens:", error);
      toast.error("Erro ao carregar mensagens");
      setMessageRows([]);
    } finally {
      setLoading(false);
    }
  };
  
  const formatPhoneNumber = (number) => {
    if (!number) return '';
    
    if (number.length === 12 && number.startsWith('55')) {
      const ddd = number.substring(2, 4);
      const firstPart = number.substring(4, 9);
      const lastPart = number.substring(9);
      return `+55 (${ddd}) ${firstPart}-${lastPart}`;
    }
    
    return number;
  };

  const exportToExcel = async () => {
    try {
      toast.info("Gerando relatório Excel, aguarde...");
      
      let allMessagesData = [];
      let currentPage = 1;
      let hasMoreData = true;
      const pageSize = 1000;
      
      while (hasMoreData) {
        const { data: messagesResponse } = await api.get(`/maturations/${maturationId}/messages?page=${currentPage}&pageSize=${pageSize}`);
        
        if (!messagesResponse || !messagesResponse.messages) {
          break;
        }
        
        allMessagesData = [...allMessagesData, ...messagesResponse.messages];
        
        hasMoreData = currentPage < messagesResponse.totalPages;
        currentPage++;
      }
      
      if (allMessagesData.length === 0) {
        toast.error("Nenhum dado encontrado para exportar");
        return;
      }

      const excelData = [
        ["Relatório de Maturação", "", "", "", "", "", ""],
        [`Maturação: ${maturation.name}`, "", "", "", "", "", ""],
        [`Data de Geração: ${new Date().toLocaleString('pt-BR')}`, "", "", "", "", "", ""],
        ["", "", "", "", "", "", ""],
        ["Nome", "Número", "Operadora", "Enviadas", "Recebidas", "Primeiro Envio", "Último Envio"],
      ];

      allMessagesData.forEach(item => {
        excelData.push([
          item.name || '',
          item.number || '',
          item.carrier || '',
          item.sentCount || 0,
          item.receivedCount || 0,
          item.firstSentAt ? new Date(item.firstSentAt).toLocaleString('pt-BR') : '',
          item.lastSentAt ? new Date(item.lastSentAt).toLocaleString('pt-BR') : ''
        ]);
      });

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.aoa_to_sheet(excelData);

      ws['!cols'] = [
        { wch: 20 },
        { wch: 20 },
        { wch: 15 },
        { wch: 12 },
        { wch: 12 },
        { wch: 20 },
        { wch: 20 }
      ];

      XLSX.utils.book_append_sheet(wb, ws, "Relatório de Maturação");

      const fileName = `relatorio_maturacao_${maturation.name?.replace(/[^a-zA-Z0-9]/g, '_')}_${new Date().toISOString().split('T')[0]}.xlsx`;

      XLSX.writeFile(wb, fileName);
      
      toast.success(`Relatório Excel gerado com sucesso! ${allMessagesData.length} registros exportados.`);
      
    } catch (error) {
      console.error("Erro ao exportar para Excel:", error);
      toast.error("Erro ao gerar relatório Excel");
    }
  };

  return (
    <MainContainer>
      <MainHeader>
        <Grid style={{ width: "99.6%" }} container>
          <Grid xs={12} item style={{ display: "flex", alignItems: "center" }}>
            <Button
              variant="outlined"
              color="primary"
              style={{ marginRight: 10 }}
              onClick={() => history.push('/maturations')}
              startIcon={<ArrowBackIcon />}
            >
              Voltar
            </Button>
            <Title>📊 Relatório de {maturation.name || "Maturação"}</Title>
            <Box style={{ marginLeft: "auto" }}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<GetAppIcon />}
                onClick={exportToExcel}
              >
                Exportar Excel
              </Button>
            </Box>
          </Grid>
        </Grid>
      </MainHeader>
      
      <Paper className={classes.mainPaper} variant="outlined">
        <Box mb={2}>
          <Typography variant="h5" component="h2" gutterBottom>
            {maturation.name || "Maturação"}
          </Typography>
          <Chip 
            label={formatStatus(maturation.status)} 
            color={maturation.status === "FINALIZADA" ? "primary" : "default"}
            style={{ fontWeight: 'bold', marginRight: 8 }}
          />
        </Box>
        
        <Grid container spacing={3} className={classes.summaryCards}>
          {/* Card de Status */}
          <Grid item xs={12} md={6} lg={3}>
            <Card className={classes.summaryCard} variant="outlined">
              <CardContent>
                <Typography variant="h6" color="textSecondary" gutterBottom>
                  Status de Entrega
                </Typography>
                <Box className={classes.pieChartContainer}>
                  <Doughnut data={chartData} options={chartOptions} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          {/* Cards com estatísticas principais */}
          <Grid item xs={12} md={6} lg={3}>
            <Card className={classes.summaryCard} variant="outlined">
              <CardContent>
                <SendIcon className={classes.cardIcon} />
                <Typography variant="h4" component="div">
                  {totalSent}
                </Typography>
                <Typography color="textSecondary">
                  Mensagens Enviadas
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={6} lg={3}>
            <Card className={classes.summaryCard} variant="outlined">
              <CardContent>
                <ReplyIcon className={classes.cardIcon} />
                <Typography variant="h4" component="div">
                  {totalReceived}
                </Typography>
                <Typography color="textSecondary">
                  Mensagens Recebidas
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={6} lg={3}>
            <Card className={classes.summaryCard} variant="outlined">
              <CardContent>
                <PhoneIcon className={classes.cardIcon} />
                <Typography variant="h4" component="div">
                  {uniqueNumbers}
                </Typography>
                <Typography color="textSecondary">
                  Contatos Únicos
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
        
        {/* Barra de progresso */}
        <Box className={classes.progressContainer}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs>
              <Typography variant="body2" color="textSecondary">Taxa de Entrega</Typography>
            </Grid>
            <Grid item>
              <Typography variant="body2" color="textPrimary">
                {totalDelivered} de {totalSent || 1}
              </Typography>
            </Grid>
          </Grid>
          <LinearProgress
            variant="determinate"
            style={{ height: 10, borderRadius: 5, margin: "8px 0" }}
            value={(totalDelivered / (totalSent || 1)) * 100}
          />
        </Box>

        {/* Gráfico de barras - Envios vs Recebimentos */}
        <Box className={classes.chartContainer}>
          <Typography variant="h6" className={classes.sectionTitle}>
            Envios vs Recebimentos por Número (Top 10)
          </Typography>
          <Bar data={barChartData} options={barChartOptions} />
        </Box>
        
        {/* Informações adicionais da maturação */}
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Card variant="outlined" className={classes.detailsCard}>
              <CardHeader
                avatar={
                  <Avatar className={classes.avatar}>
                    <WhatsAppIcon />
                  </Avatar>
                }
                title="Conexão WhatsApp"
                subheader={maturation.whatsapp?.name || "Não especificado"}
              />
            </Card>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Card variant="outlined" className={classes.detailsCard}>
              <CardHeader
                avatar={
                  <Avatar className={classes.avatar}>
                    <ScheduleIcon />
                  </Avatar>
                }
                title="Data de Início"
                subheader={maturation.scheduledAt ? datetimeToClient(maturation.scheduledAt) : "Não agendado"}
              />
            </Card>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Card variant="outlined" className={classes.detailsCard}>
              <CardHeader
                avatar={
                  <Avatar className={classes.avatar}>
                    <EventAvailableIcon />
                  </Avatar>
                }
                title="Data de Término"
                subheader={maturation.endAt ? datetimeToClient(maturation.endAt) : "Sem data de término"}
              />
            </Card>
          </Grid>
        </Grid>
        
        <Divider style={{ margin: "24px 0" }} />
        
        {/* Tabela de mensagens por número */}
        <Typography variant="h6" className={classes.tableTitle}>
          Detalhamento por Número
        </Typography>
        
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell><strong>Nome</strong></TableCell>
                <TableCell><strong>Número</strong></TableCell>
                <TableCell><strong>Operadora</strong></TableCell>
                <TableCell align="center"><strong>Enviadas</strong></TableCell>
                <TableCell align="center"><strong>Recebidas</strong></TableCell>
                <TableCell align="center"><strong>Primeiro Envio</strong></TableCell>
                <TableCell align="center"><strong>Último Envio</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {messageRows.map((row, index) => (
                <TableRow key={row.contactId || index}>
                  <TableCell>{row.name || "—"}</TableCell>
                  <TableCell>{formatPhoneNumber(row.number)}</TableCell>
                  <TableCell>
                    {row.carrier && (
                      <Chip label={row.carrier} size="small" />
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Chip 
                      label={row.sentCount || 0} 
                      size="small" 
                      color="primary"
                      icon={<SendIcon />}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Chip 
                      label={row.receivedCount || 0} 
                      size="small" 
                      color="secondary"
                      icon={<ReplyIcon />}
                    />
                  </TableCell>
                  <TableCell align="center">
                    {row.firstSentAt ? datetimeToClient(row.firstSentAt) : "—"}
                  </TableCell>
                  <TableCell align="center">
                    {row.lastSentAt ? datetimeToClient(row.lastSentAt) : "—"}
                  </TableCell>
                </TableRow>
              ))}
              {messageRows.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography variant="body2" color="textSecondary">
                      Nenhuma mensagem encontrada
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        <TablePagination
          component="div"
          count={totalPages * rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage="Por página:"
        />
      </Paper>
    </MainContainer>
  );
};

export default MaturationReport;

